// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { CSSProperties } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '@/styles/datepicker.css';

import { Text } from '@/components/Themed';
import { datePickerColors } from '@/components/datePickerColors';
import appColors, { alpha } from '@/theme/colors';

// The skin's colours, handed to styles/datepicker.css as custom properties.
const pickerColors = datePickerColors(appColors) as CSSProperties;

type Props = {
  open: boolean;
  date: Date;
  onConfirm: (date: Date) => void;
  onCancel: () => void;
  closeLabel?: string;
  mode?: 'date' | 'datetime';
};

export default function PlatformDatePicker({ open, date, onConfirm, onCancel, closeLabel = 'Close', mode = 'date' }: Props) {
  const isDateTime = mode === 'datetime';
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.backdrop} onPress={onCancel}>
        <Pressable style={styles.card} onPress={() => {}}>
          <div className="goffy-date-picker" style={pickerColors}>
            <DatePicker
              selected={date}
              onChange={(value: Date | null) => {
                if (!value) return;
                onConfirm(value);
              }}
              showTimeSelect={isDateTime}
              dateFormat={isDateTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'}
              timeFormat="HH:mm"
              inline
            />
          </div>
          <Pressable style={styles.closeButton} onPress={onCancel}>
            <Text style={styles.closeText}>{closeLabel}</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.16),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panel,
    padding: 12,
    gap: 10,
  },
  closeButton: {
    alignSelf: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: appColors.border.strong,
    borderWidth: 1,
    borderColor: appColors.border.accent,
  },
  closeText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
});

